<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import type { EditorState } from '../editor-state.svelte';
  import { declareLayers, declareParams, monaco } from '../monaco';

  const { editor }: { editor: EditorState } = $props();

  let host: HTMLDivElement;
  let model: monaco.editor.ITextModel | null = $state.raw(null);
  let code: monaco.editor.IStandaloneCodeEditor | null = $state.raw(null);

  const dark = window.matchMedia('(prefers-color-scheme: dark)');
  const applyTheme = () => monaco.editor.setTheme(dark.matches ? 'vs-dark' : 'vs');

  onMount(() => {
    const text = monaco.editor.createModel(
      editor.source,
      'javascript',
      monaco.Uri.parse('file:///drawing.js'),
    );
    const view = monaco.editor.create(host, {
      model: text,
      automaticLayout: true,
      minimap: { enabled: false },
      fontSize: 13,
      scrollBeyondLastLine: false,
      tabSize: 2,
      ariaLabel: 'Drawing script',
    });
    applyTheme();
    dark.addEventListener('change', applyTheme);
    const typing = text.onDidChangeContent(() => editor.typeSource(text.getValue()));
    // Only a cursor the person moved highlights its line's shapes; typing and leaving clear it.
    const moving = view.onDidChangeCursorPosition(({ reason, source, position }) => {
      const moved = reason === monaco.editor.CursorChangeReason.Explicit && source !== 'api';
      editor.cursorLine = moved ? position.lineNumber : null;
    });
    const leaving = view.onDidBlurEditorText(() => (editor.cursorLine = null));
    // The drawing has one undo history, so these keys step through it, not the text's own.
    const { CtrlCmd, Shift } = monaco.KeyMod;
    view.addCommand(CtrlCmd | monaco.KeyCode.KeyZ, () => editor.undo());
    view.addCommand(CtrlCmd | Shift | monaco.KeyCode.KeyZ, () => editor.redo());
    view.addCommand(CtrlCmd | monaco.KeyCode.KeyY, () => editor.redo());
    model = text;
    code = view;
    return () => {
      dark.removeEventListener('change', applyTheme);
      typing.dispose();
      moving.dispose();
      leaving.dispose();
      view.dispose();
      text.dispose();
    };
  });

  // Another drawing opened: show its script, with a fresh undo history.
  $effect(() => {
    void editor.documentId;
    const text = model;
    if (!text) return;
    const source = untrack(() => editor.source);
    if (text.getValue() !== source) text.setValue(source);
  });

  // The script changed other than by typing here, such as an undo or Claude's edit: change
  // only the text that differs, leaving the cursor after it.
  $effect(() => {
    void editor.replaced;
    const text = model;
    if (!text) return;
    const source = untrack(() => editor.source);
    const old = text.getValue();
    if (old === source) return;
    let start = 0;
    while (start < old.length && old[start] === source[start]) start += 1;
    let end = 0;
    while (
      end < old.length - start &&
      end < source.length - start &&
      old[old.length - 1 - end] === source[source.length - 1 - end]
    ) {
      end += 1;
    }
    const from = text.getPositionAt(start);
    const to = text.getPositionAt(old.length - end);
    text.applyEdits([
      {
        range: monaco.Range.fromPositions(from, to),
        text: source.slice(start, source.length - end),
      },
    ]);
    const cursor = text.getPositionAt(source.length - end);
    code?.setPosition(cursor);
    code?.revealPositionInCenterIfOutsideViewport(cursor);
  });

  // A shape picked in the Objects list: select its line and scroll it into view, without
  // taking focus, so keys still reach the canvas. Asked from Properties, it takes focus, and
  // lays out first, since the editor may have been hidden until now.
  $effect(() => {
    const shown = editor.sourceLine;
    if (!shown || !code || !model || shown.line > model.getLineCount()) return;
    if (shown.focus) code.layout();
    code.setSelection({
      startLineNumber: shown.line,
      startColumn: 1,
      endLineNumber: shown.line,
      endColumn: model.getLineMaxColumn(shown.line),
    });
    code.revealLineInCenterIfOutsideViewport(shown.line);
    if (shown.focus) code.focus();
  });

  $effect(() => {
    declareParams(editor.params);
  });

  $effect(() => {
    declareLayers(editor.layers);
  });

  $effect(() => {
    const error = editor.scriptError;
    if (!model) return;
    const markers =
      error?.line != null
        ? [
            {
              severity: monaco.MarkerSeverity.Error,
              message: error.message,
              startLineNumber: error.line,
              startColumn: error.column ?? 1,
              endLineNumber: error.line,
              endColumn:
                error.column === null ? model.getLineMaxColumn(error.line) : error.column + 1,
            },
          ]
        : [];
    monaco.editor.setModelMarkers(model, 'draw-run', markers);
  });
</script>

<div class="code" data-own-keys bind:this={host}></div>

<style>
  .code {
    flex: 1;
    min-height: 0;
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    overflow: hidden;
  }
</style>
